import type {
  OfflineCacheEntry,
  OfflineMovementOperation,
  OfflinePersistenceAdapter,
} from "./offline";

export const PRODUCTION_PBKDF2_ITERATIONS = 600_000;
export const SECURE_VAULT_LOCAL_VERSION = 1 as const;
export const SECURE_VAULT_SNAPSHOT_VERSION = 1 as const;

const MIN_PBKDF2_ITERATIONS = 100_000;
const MAX_PBKDF2_ITERATIONS = 2_000_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;
const MAX_PASSWORD_LENGTH = 4_096;
// Le champ chiffré distant est plafonné à 6 Mio. AES-GCM ajoute un tag de
// 16 octets, donc le clair reste volontairement sous ce plafond.
// Laisse 1 Kio de marge pour le base64 et les métadonnées de l’enveloppe afin
// que toute copie locale valide reste sous le plafond JSON distant de 8 Mio.
const MAX_CIPHERTEXT_BYTES = 6 * 1024 * 1024 - 1024;
const MAX_PLAINTEXT_BYTES = MAX_CIPHERTEXT_BYTES - 16;
const MAX_CACHE_ENTRIES = 20_000;
const MAX_MUTATIONS = 5_000;
const MAX_RECEIPTS = 20_000;
const MAX_CACHE_KEY_LENGTH = 512;
const MAX_IDENTIFIER_LENGTH = 512;
const MAX_JSON_DEPTH = 80;
const MAX_JSON_NODES = 1_000_000;
const ADDITIONAL_DATA = "ma-cave-app|offline-vault|local-v1";

const DATABASE_NAME = "cave-personnelle-secure-offline";
const DATABASE_VERSION = 1;
const REPLICA_STORE = "secure-replica";
const REPLICA_KEY = "current";
const LEGACY_DATABASE_NAME = "cave-personnelle-offline";
const LEGACY_CACHE_STORE = "cache";
const LEGACY_MUTATION_STORE = "mutations";
const LEGACY_RECEIPT_STORE = "receipts";

export interface SerializedVaultEnvelope {
  version: typeof SECURE_VAULT_LOCAL_VERSION;
  iterations: number;
  salt: string;
  iv: string;
  ciphertext: string;
}

export interface LocalReplica {
  localVersion: typeof SECURE_VAULT_LOCAL_VERSION;
  generation: number;
  envelope: SerializedVaultEnvelope;
  sync: {
    linked: boolean;
    baseRevision: number;
    dirty: boolean;
  };
}

export interface VaultSyncMetadata {
  linked: boolean;
  generation: number;
  baseRevision: number;
  dirty: boolean;
}

export interface SecureVaultSnapshot {
  schemaVersion: typeof SECURE_VAULT_SNAPSHOT_VERSION;
  cache: OfflineCacheEntry[];
  mutations: OfflineMovementOperation[];
  receipts: OfflineMovementOperation[];
}

export interface SecureVaultStore {
  read(): Promise<unknown | null>;
  write(replica: LocalReplica): Promise<void>;
  delete(): Promise<void>;
  readLegacySnapshot?(): Promise<SecureVaultSnapshot | null>;
  deleteLegacyStorage?(): Promise<void>;
}

export interface SecureOfflineAdapterOptions {
  crypto?: Crypto;
  iterations?: number;
}

export type VaultCredential = string | CryptoKey;

export interface ActivateSecureVaultOptions {
  initialSnapshot?: SecureVaultSnapshot;
  baseRevision?: number;
  dirty?: boolean;
}

export class SecureVaultUnavailableError extends Error {
  constructor(message = "Le coffre chiffré local est indisponible.") {
    super(message);
    this.name = "SecureVaultUnavailableError";
  }
}

export class SecureVaultLockedError extends Error {
  constructor(message = "Le coffre chiffré local est verrouillé.") {
    super(message);
    this.name = "SecureVaultLockedError";
  }
}

export class SecureVaultUnlockError extends Error {
  constructor(
    message = "Mot de passe incorrect ou copie locale endommagée.",
  ) {
    super(message);
    this.name = "SecureVaultUnlockError";
  }
}

export class SecureVaultConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SecureVaultConflictError";
  }
}

function cloneJson<T>(value: T): T {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new TypeError("Le contenu du coffre doit être sérialisable en JSON.");
  }
  return JSON.parse(serialized) as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

function boundedInteger(
  value: unknown,
  minimum: number,
  maximum: number,
): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= minimum &&
    value <= maximum
  );
}

function boundedString(
  value: unknown,
  maximum: number,
  allowEmpty = false,
): value is string {
  return (
    typeof value === "string" &&
    value.length <= maximum &&
    (allowEmpty || value.length > 0)
  );
}

function assertPassword(password: string): void {
  if (!boundedString(password, MAX_PASSWORD_LENGTH)) {
    throw new TypeError("Le mot de passe local est invalide.");
  }
}

export function assertVaultKeyMaterial(
  material: unknown,
): asserts material is CryptoKey {
  const cryptoKeyConstructor = globalThis.CryptoKey;
  const hasValidBrand =
    typeof cryptoKeyConstructor === "function"
      ? material instanceof cryptoKeyConstructor
      : Object.prototype.toString.call(material) === "[object CryptoKey]";
  if (
    material === null ||
    typeof material !== "object" ||
    !hasValidBrand
  ) {
    throw new TypeError("Le matériau cryptographique de confiance est invalide.");
  }
  const candidate = material as Partial<CryptoKey>;
  if (
    candidate.type !== "secret" ||
    candidate.extractable !== false ||
    candidate.algorithm?.name !== "PBKDF2" ||
    candidate.usages?.length !== 1 ||
    candidate.usages[0] !== "deriveKey"
  ) {
    throw new TypeError("Le matériau cryptographique de confiance est invalide.");
  }
}

function base64ToBytes(value: unknown, label: string): Uint8Array {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > Math.ceil((MAX_PLAINTEXT_BYTES + 32) / 3) * 4 ||
    !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u.test(
      value,
    )
  ) {
    throw new TypeError(`${label} n’est pas un encodage base64 valide.`);
  }
  let binary: string;
  try {
    binary = globalThis.atob(value);
  } catch {
    throw new TypeError(`${label} n’est pas un encodage base64 valide.`);
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  if (bytesToBase64(bytes) !== value) {
    throw new TypeError(`${label} n’est pas un base64 RFC 4648 canonique.`);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 0x8000;
  let binary = "";
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return globalThis.btoa(binary);
}

function cryptoBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= left[index] ^ right[index];
  }
  return difference === 0;
}

function assertJsonValue(
  value: unknown,
  depth: number,
  counter: { nodes: number },
): void {
  counter.nodes += 1;
  if (counter.nodes > MAX_JSON_NODES || depth > MAX_JSON_DEPTH) {
    throw new TypeError("Le contenu du coffre dépasse les limites autorisées.");
  }
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError("Le coffre contient un nombre JSON invalide.");
    }
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) assertJsonValue(entry, depth + 1, counter);
    return;
  }
  if (!isRecord(value)) {
    throw new TypeError("Le coffre contient une valeur non JSON.");
  }
  for (const [key, entry] of Object.entries(value)) {
    if (key.length > MAX_CACHE_KEY_LENGTH) {
      throw new TypeError("Le coffre contient une propriété trop longue.");
    }
    assertJsonValue(entry, depth + 1, counter);
  }
}

function assertCacheEntry(value: unknown): asserts value is OfflineCacheEntry {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["key", "value", "cached_at"]) ||
    !boundedString(value.key, MAX_CACHE_KEY_LENGTH) ||
    !boundedInteger(value.cached_at, 0, Number.MAX_SAFE_INTEGER)
  ) {
    throw new TypeError("Une entrée de cache du coffre est invalide.");
  }
  assertJsonValue(value.value, 0, { nodes: 0 });
}

function assertMovementOperation(
  value: unknown,
): asserts value is OfflineMovementOperation {
  if (
    !isRecord(value) ||
    !boundedString(value.client_operation_id, MAX_IDENTIFIER_LENGTH) ||
    !boundedString(value.wine_id, MAX_IDENTIFIER_LENGTH) ||
    !boundedInteger(value.sequence, 1, Number.MAX_SAFE_INTEGER) ||
    !boundedInteger(value.attempts, 0, Number.MAX_SAFE_INTEGER) ||
    !["pending", "sync", "error"].includes(String(value.status)) ||
    !["boire", "correction"].includes(String(value.action)) ||
    !isRecord(value.payload) ||
    !isRecord(value.temporary_movement)
  ) {
    throw new TypeError("Une mutation du coffre est invalide.");
  }
  assertJsonValue(value, 0, { nodes: 0 });
}

function assertSnapshot(value: unknown): asserts value is SecureVaultSnapshot {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["schemaVersion", "cache", "mutations", "receipts"]) ||
    value.schemaVersion !== SECURE_VAULT_SNAPSHOT_VERSION ||
    !Array.isArray(value.cache) ||
    !Array.isArray(value.mutations) ||
    !Array.isArray(value.receipts) ||
    value.cache.length > MAX_CACHE_ENTRIES ||
    value.mutations.length > MAX_MUTATIONS ||
    value.receipts.length > MAX_RECEIPTS
  ) {
    throw new TypeError("Le schéma du coffre local n’est pas reconnu.");
  }

  const cacheKeys = new Set<string>();
  for (const entry of value.cache) {
    assertCacheEntry(entry);
    if (cacheKeys.has(entry.key)) {
      throw new TypeError("Le coffre contient une clé de cache dupliquée.");
    }
    cacheKeys.add(entry.key);
  }

  for (const [label, operations] of [
    ["mutation", value.mutations],
    ["reçu", value.receipts],
  ] as const) {
    const operationIds = new Set<string>();
    for (const operation of operations) {
      assertMovementOperation(operation);
      if (operationIds.has(operation.client_operation_id)) {
        throw new TypeError(`Le coffre contient un ${label} dupliqué.`);
      }
      operationIds.add(operation.client_operation_id);
    }
  }

  const serialized = JSON.stringify(value);
  if (new TextEncoder().encode(serialized).byteLength > MAX_PLAINTEXT_BYTES) {
    throw new TypeError("Le coffre local dépasse la taille maximale autorisée.");
  }
}

function validateEnvelope(value: unknown): SerializedVaultEnvelope {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "version",
      "iterations",
      "salt",
      "iv",
      "ciphertext",
    ]) ||
    value.version !== SECURE_VAULT_LOCAL_VERSION ||
    !boundedInteger(
      value.iterations,
      MIN_PBKDF2_ITERATIONS,
      MAX_PBKDF2_ITERATIONS,
    )
  ) {
    throw new TypeError("L’enveloppe chiffrée locale est invalide.");
  }
  const salt = base64ToBytes(value.salt, "Le sel PBKDF2");
  const iv = base64ToBytes(value.iv, "L’IV AES-GCM");
  const ciphertext = base64ToBytes(value.ciphertext, "Le texte chiffré");
  if (
    salt.byteLength < SALT_BYTES ||
    salt.byteLength > 64 ||
    iv.byteLength !== IV_BYTES ||
    ciphertext.byteLength < 16 ||
    ciphertext.byteLength > MAX_CIPHERTEXT_BYTES
  ) {
    throw new TypeError("Les paramètres cryptographiques locaux sont invalides.");
  }
  return cloneJson(value) as unknown as SerializedVaultEnvelope;
}

export function validateLocalReplica(value: unknown): LocalReplica {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["localVersion", "generation", "envelope", "sync"]) ||
    value.localVersion !== SECURE_VAULT_LOCAL_VERSION ||
    !boundedInteger(value.generation, 1, Number.MAX_SAFE_INTEGER) ||
    !isRecord(value.sync) ||
    !hasExactKeys(value.sync, ["linked", "baseRevision", "dirty"]) ||
    typeof value.sync.linked !== "boolean" ||
    typeof value.sync.dirty !== "boolean" ||
    !boundedInteger(value.sync.baseRevision, 0, Number.MAX_SAFE_INTEGER) ||
    (value.sync.linked && value.sync.baseRevision < 1) ||
    (!value.sync.linked && value.sync.baseRevision !== 0)
  ) {
    throw new TypeError("La copie locale chiffrée est invalide.");
  }
  return cloneJson({
    localVersion: SECURE_VAULT_LOCAL_VERSION,
    generation: value.generation,
    envelope: validateEnvelope(value.envelope),
    sync: value.sync,
  }) as LocalReplica;
}

function emptySnapshot(): SecureVaultSnapshot {
  return {
    schemaVersion: SECURE_VAULT_SNAPSHOT_VERSION,
    cache: [],
    mutations: [],
    receipts: [],
  };
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Lecture IndexedDB impossible."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Écriture IndexedDB impossible."));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("Écriture IndexedDB annulée."));
  });
}

export class IndexedDbSecureVaultStore implements SecureVaultStore {
  private databasePromise: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly factory: IDBFactory,
    private readonly databaseName = DATABASE_NAME,
  ) {}

  private database(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise;
    this.databasePromise = new Promise((resolve, reject) => {
      const request = this.factory.open(this.databaseName, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(REPLICA_STORE)) {
          database.createObjectStore(REPLICA_STORE);
        }
      };
      request.onsuccess = () => {
        const database = request.result;
        database.onversionchange = () => database.close();
        resolve(database);
      };
      request.onerror = () =>
        reject(request.error ?? new Error("Ouverture IndexedDB impossible."));
      request.onblocked = () =>
        reject(new Error("Mise à niveau du coffre local bloquée."));
    });
    return this.databasePromise;
  }

  async read(): Promise<unknown | null> {
    const transaction = (await this.database()).transaction(
      REPLICA_STORE,
      "readonly",
    );
    const value = await requestResult(
      transaction.objectStore(REPLICA_STORE).get(REPLICA_KEY),
    );
    return value === undefined ? null : cloneJson(value);
  }

  async write(replica: LocalReplica): Promise<void> {
    const transaction = (await this.database()).transaction(
      REPLICA_STORE,
      "readwrite",
    );
    transaction
      .objectStore(REPLICA_STORE)
      .put(cloneJson(replica), REPLICA_KEY);
    await transactionDone(transaction);
  }

  async delete(): Promise<void> {
    const transaction = (await this.database()).transaction(
      REPLICA_STORE,
      "readwrite",
    );
    transaction.objectStore(REPLICA_STORE).delete(REPLICA_KEY);
    await transactionDone(transaction);
  }

  private openLegacyDatabase(): Promise<IDBDatabase | null> {
    return new Promise((resolve, reject) => {
      let databaseWasCreated = false;
      const request = this.factory.open(LEGACY_DATABASE_NAME);
      request.onupgradeneeded = (event) => {
        // Ne créons jamais une ancienne base vide uniquement pour la migrer.
        databaseWasCreated = event.oldVersion === 0;
        request.transaction?.abort();
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        if (databaseWasCreated) resolve(null);
        else {
          reject(
            request.error ??
              new Error("Ouverture de l’ancienne copie locale impossible."),
          );
        }
      };
      request.onblocked = () =>
        reject(new Error("Lecture de l’ancienne copie locale bloquée."));
    });
  }

  async readLegacySnapshot(): Promise<SecureVaultSnapshot | null> {
    const database = await this.openLegacyDatabase();
    if (!database) return null;
    try {
      if (
        !database.objectStoreNames.contains(LEGACY_CACHE_STORE) ||
        !database.objectStoreNames.contains(LEGACY_MUTATION_STORE) ||
        !database.objectStoreNames.contains(LEGACY_RECEIPT_STORE)
      ) {
        return null;
      }
      const transaction = database.transaction(
        [LEGACY_CACHE_STORE, LEGACY_MUTATION_STORE, LEGACY_RECEIPT_STORE],
        "readonly",
      );
      const completed = transactionDone(transaction);
      const [cache, mutations, receipts] = await Promise.all([
        requestResult(
          transaction.objectStore(LEGACY_CACHE_STORE).getAll() as IDBRequest<
            OfflineCacheEntry[]
          >,
        ),
        requestResult(
          transaction
            .objectStore(LEGACY_MUTATION_STORE)
            .getAll() as IDBRequest<OfflineMovementOperation[]>,
        ),
        requestResult(
          transaction
            .objectStore(LEGACY_RECEIPT_STORE)
            .getAll() as IDBRequest<OfflineMovementOperation[]>,
        ),
      ]);
      await completed;
      const snapshot: SecureVaultSnapshot = {
        schemaVersion: SECURE_VAULT_SNAPSHOT_VERSION,
        cache,
        mutations,
        receipts,
      };
      assertSnapshot(snapshot);
      return cloneJson(snapshot);
    } finally {
      database.close();
    }
  }

  async deleteLegacyStorage(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const request = this.factory.deleteDatabase(LEGACY_DATABASE_NAME);
      request.onsuccess = () => resolve();
      request.onerror = () =>
        reject(
          request.error ??
            new Error("Suppression de l’ancienne copie locale impossible."),
        );
      request.onblocked = () =>
        reject(new Error("Suppression de l’ancienne copie locale bloquée."));
    });
  }
}

export class MemorySecureVaultStore implements SecureVaultStore {
  private replica: unknown | null = null;

  async read(): Promise<unknown | null> {
    return this.replica === null ? null : cloneJson(this.replica);
  }

  async write(replica: LocalReplica): Promise<void> {
    this.replica = cloneJson(replica);
  }

  async delete(): Promise<void> {
    this.replica = null;
  }

  async replaceRawForTest(value: unknown | null): Promise<void> {
    this.replica = value === null ? null : cloneJson(value);
  }
}

export class SecureOfflineAdapter implements OfflinePersistenceAdapter {
  private readonly configuredCrypto: Crypto | undefined;
  private readonly iterations: number;
  private plaintext: SecureVaultSnapshot | null = null;
  private replica: LocalReplica | null = null;
  private key: CryptoKey | null = null;
  private material: CryptoKey | null = null;
  private waiters = new Set<() => void>();
  private mutationQueue: Promise<void> = Promise.resolve();

  constructor(
    private readonly store: SecureVaultStore,
    options: SecureOfflineAdapterOptions = {},
  ) {
    const iterations = options.iterations ?? PRODUCTION_PBKDF2_ITERATIONS;
    if (
      !boundedInteger(
        iterations,
        MIN_PBKDF2_ITERATIONS,
        MAX_PBKDF2_ITERATIONS,
      )
    ) {
      throw new TypeError("Le nombre d’itérations PBKDF2 est hors limites.");
    }
    this.configuredCrypto = options.crypto;
    this.iterations = iterations;
  }

  private crypto(): Crypto {
    const crypto = this.configuredCrypto ?? globalThis.crypto;
    if (
      !crypto ||
      typeof crypto.getRandomValues !== "function" ||
      !crypto.subtle
    ) {
      throw new SecureVaultUnavailableError(
        "Web Crypto est requis pour ouvrir le coffre local.",
      );
    }
    return crypto;
  }

  private randomBytes(length: number): Uint8Array {
    return this.crypto().getRandomValues(new Uint8Array(length));
  }

  private async importPasswordMaterial(password: string): Promise<CryptoKey> {
    assertPassword(password);
    return this.crypto().subtle.importKey(
      "raw",
      new TextEncoder().encode(password),
      "PBKDF2",
      false,
      ["deriveKey"],
    );
  }

  private async keyMaterial(credential: VaultCredential): Promise<CryptoKey> {
    if (typeof credential === "string") {
      return this.importPasswordMaterial(credential);
    }
    assertVaultKeyMaterial(credential);
    return credential;
  }

  private async deriveKey(
    material: CryptoKey,
    salt: Uint8Array,
    iterations: number,
  ): Promise<CryptoKey> {
    const crypto = this.crypto();
    assertVaultKeyMaterial(material);
    return crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        hash: "SHA-256",
        salt: cryptoBuffer(salt),
        iterations,
      },
      material,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  }

  private freshIv(previous?: string): Uint8Array {
    const previousBytes = previous
      ? base64ToBytes(previous, "L’ancien IV AES-GCM")
      : null;
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const iv = this.randomBytes(IV_BYTES);
      if (!previousBytes || !equalBytes(iv, previousBytes)) return iv;
    }
    throw new SecureVaultUnavailableError(
      "Impossible de générer un IV AES-GCM unique.",
    );
  }

  private async encrypt(
    snapshot: SecureVaultSnapshot,
    key: CryptoKey,
    salt: Uint8Array,
    iterations: number,
    previousIv?: string,
  ): Promise<SerializedVaultEnvelope> {
    const normalized = cloneJson(snapshot);
    assertSnapshot(normalized);
    const plaintext = new TextEncoder().encode(JSON.stringify(normalized));
    if (plaintext.byteLength > MAX_PLAINTEXT_BYTES) {
      throw new TypeError("Le coffre local dépasse la taille maximale autorisée.");
    }
    const iv = this.freshIv(previousIv);
    const encrypted = await this.crypto().subtle.encrypt(
      {
        name: "AES-GCM",
        iv: cryptoBuffer(iv),
        additionalData: new TextEncoder().encode(ADDITIONAL_DATA),
        tagLength: 128,
      },
      key,
      plaintext,
    );
    return {
      version: SECURE_VAULT_LOCAL_VERSION,
      iterations,
      salt: bytesToBase64(salt),
      iv: bytesToBase64(iv),
      ciphertext: bytesToBase64(new Uint8Array(encrypted)),
    };
  }

  private async decrypt(
    envelopeValue: unknown,
    credential: VaultCredential,
  ): Promise<{
    envelope: SerializedVaultEnvelope;
    snapshot: SecureVaultSnapshot;
    key: CryptoKey;
    material: CryptoKey;
  }> {
    try {
      const envelope = validateEnvelope(envelopeValue);
      const salt = base64ToBytes(envelope.salt, "Le sel PBKDF2");
      const material = await this.keyMaterial(credential);
      const key = await this.deriveKey(
        material,
        salt,
        envelope.iterations,
      );
      const decrypted = await this.crypto().subtle.decrypt(
        {
          name: "AES-GCM",
          iv: cryptoBuffer(base64ToBytes(envelope.iv, "L’IV AES-GCM")),
          additionalData: new TextEncoder().encode(ADDITIONAL_DATA),
          tagLength: 128,
        },
        key,
        cryptoBuffer(
          base64ToBytes(envelope.ciphertext, "Le texte chiffré"),
        ),
      );
      const json = new TextDecoder("utf-8", { fatal: true }).decode(decrypted);
      const snapshot = JSON.parse(json) as unknown;
      assertSnapshot(snapshot);
      return { envelope, snapshot: cloneJson(snapshot), key, material };
    } catch (error) {
      if (error instanceof SecureVaultUnavailableError) throw error;
      throw new SecureVaultUnlockError();
    }
  }

  private signalUnlocked(): void {
    const waiters = [...this.waiters];
    this.waiters.clear();
    for (const resolve of waiters) resolve();
  }

  private async waitUntilUnlocked(): Promise<void> {
    while (!this.plaintext || !this.replica || !this.key) {
      await new Promise<void>((resolve) => this.waiters.add(resolve));
    }
  }

  private async settleWrites(): Promise<void> {
    await this.mutationQueue;
  }

  private serialize<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.mutationQueue.then(operation);
    this.mutationQueue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  private setSession(
    replica: LocalReplica,
    snapshot: SecureVaultSnapshot,
    key: CryptoKey,
    material: CryptoKey,
  ): void {
    assertVaultKeyMaterial(material);
    this.replica = cloneJson(replica);
    this.plaintext = cloneJson(snapshot);
    this.key = key;
    this.material = material;
    this.signalUnlocked();
  }

  private clearSession(): void {
    this.plaintext = null;
    this.replica = null;
    this.key = null;
    this.material = null;
  }

  private async rawReplica(): Promise<LocalReplica | null> {
    const value = await this.store.read();
    return value === null ? null : validateLocalReplica(value);
  }

  private async legacySnapshot(): Promise<SecureVaultSnapshot | null> {
    if (!this.store.readLegacySnapshot) return null;
    return this.store.readLegacySnapshot();
  }

  private async removeLegacyStorage(): Promise<void> {
    if (this.store.deleteLegacyStorage) {
      await this.store.deleteLegacyStorage();
    }
  }

  private async createStagingReplica(
    credential: VaultCredential,
    snapshotValue: SecureVaultSnapshot,
  ): Promise<LocalReplica> {
    const snapshot = cloneJson(snapshotValue);
    assertSnapshot(snapshot);
    const salt = this.randomBytes(SALT_BYTES);
    const material = await this.keyMaterial(credential);
    const key = await this.deriveKey(material, salt, this.iterations);
    const envelope = await this.encrypt(
      snapshot,
      key,
      salt,
      this.iterations,
    );
    const replica: LocalReplica = {
      localVersion: SECURE_VAULT_LOCAL_VERSION,
      generation: 1,
      envelope,
      sync: { linked: false, baseRevision: 0, dirty: true },
    };
    await this.store.write(replica);
    await this.removeLegacyStorage();
    this.setSession(replica, snapshot, key, material);
    return cloneJson(replica);
  }

  async hasReplica(): Promise<boolean> {
    await this.settleWrites();
    return (await this.rawReplica()) !== null;
  }

  async hasLinkedReplica(): Promise<boolean> {
    await this.settleWrites();
    return (await this.rawReplica())?.sync.linked === true;
  }

  isUnlocked(): boolean {
    return Boolean(this.plaintext && this.replica && this.key);
  }

  async activate(
    credential: VaultCredential,
    options: ActivateSecureVaultOptions = {},
  ): Promise<LocalReplica> {
    if (
      (options.baseRevision !== undefined && options.baseRevision !== 0) ||
      (options.dirty !== undefined && options.dirty !== true)
    ) {
      throw new TypeError(
        "Une activation locale doit commencer non liée et à synchroniser.",
      );
    }
    return this.serialize(async () => {
      if ((await this.rawReplica()) !== null) {
        throw new SecureVaultConflictError(
          "Une copie locale existe déjà sur cet appareil.",
        );
      }
      const migrated = options.initialSnapshot
        ? null
        : await this.legacySnapshot();
      const snapshot = cloneJson(
        options.initialSnapshot ?? migrated ?? emptySnapshot(),
      );
      return this.createStagingReplica(credential, snapshot);
    });
  }

  async migrateLegacyReplica(
    credential: VaultCredential,
  ): Promise<LocalReplica | null> {
    return this.serialize(async () => {
      if ((await this.rawReplica()) !== null) {
        throw new SecureVaultConflictError(
          "Une copie locale chiffrée existe déjà sur cet appareil.",
        );
      }
      const legacy = await this.legacySnapshot();
      if (
        !legacy ||
        (legacy.cache.length === 0 &&
          legacy.mutations.length === 0 &&
          legacy.receipts.length === 0)
      ) {
        return null;
      }
      return this.createStagingReplica(credential, legacy);
    });
  }

  async unlock(credential: VaultCredential): Promise<VaultSyncMetadata> {
    return this.serialize(async () => {
      const replica = await this.rawReplica();
      if (!replica) {
        throw new SecureVaultUnlockError("Aucune copie locale à ouvrir.");
      }
      const decrypted = await this.decrypt(replica.envelope, credential);
      await this.removeLegacyStorage();
      this.setSession(
        replica,
        decrypted.snapshot,
        decrypted.key,
        decrypted.material,
      );
      return this.metadata(replica);
    });
  }

  getKeyMaterial(): CryptoKey {
    if (!this.material || !this.isUnlocked()) {
      throw new SecureVaultLockedError();
    }
    assertVaultKeyMaterial(this.material);
    return this.material;
  }

  async lock(): Promise<void> {
    await this.serialize(async () => {
      this.clearSession();
    });
  }

  async forget(): Promise<void> {
    await this.serialize(async () => {
      try {
        await this.store.delete();
        await this.removeLegacyStorage();
      } finally {
        this.clearSession();
      }
    });
  }

  async exportReplica(): Promise<LocalReplica | null> {
    await this.settleWrites();
    const replica = await this.rawReplica();
    return replica ? cloneJson(replica) : null;
  }

  private assertRemoteRevision(revision: number): void {
    if (!boundedInteger(revision, 1, Number.MAX_SAFE_INTEGER)) {
      throw new TypeError("La révision distante est invalide.");
    }
  }

  async adoptReplica(
    envelopeValue: SerializedVaultEnvelope,
    revision: number,
    credential: VaultCredential,
    generation = 1,
  ): Promise<LocalReplica> {
    this.assertRemoteRevision(revision);
    if (!boundedInteger(generation, 1, Number.MAX_SAFE_INTEGER)) {
      throw new TypeError("La génération distante est invalide.");
    }
    return this.serialize(async () => {
      if ((await this.rawReplica()) !== null) {
        throw new SecureVaultConflictError(
          "La copie distante ne peut pas remplacer une copie locale existante.",
        );
      }
      const decrypted = await this.decrypt(envelopeValue, credential);
      const replica: LocalReplica = {
        localVersion: SECURE_VAULT_LOCAL_VERSION,
        generation,
        envelope: decrypted.envelope,
        sync: { linked: true, baseRevision: revision, dirty: false },
      };
      await this.store.write(replica);
      await this.removeLegacyStorage();
      this.setSession(
        replica,
        decrypted.snapshot,
        decrypted.key,
        decrypted.material,
      );
      return cloneJson(replica);
    });
  }

  async replaceFromRemote(
    envelopeValue: SerializedVaultEnvelope,
    revision: number,
    credential: VaultCredential | null = this.material,
  ): Promise<LocalReplica> {
    this.assertRemoteRevision(revision);
    if (!credential) throw new SecureVaultLockedError();
    return this.serialize(async () => {
      const current = await this.rawReplica();
      if (!current) {
        throw new SecureVaultConflictError(
          "Aucune copie locale ne peut recevoir la révision distante.",
        );
      }
      if (current.sync.dirty) {
        throw new SecureVaultConflictError(
          "La copie locale contient des changements non confirmés.",
        );
      }
      const decrypted = await this.decrypt(envelopeValue, credential);
      const replica: LocalReplica = {
        localVersion: SECURE_VAULT_LOCAL_VERSION,
        generation: Math.max(current.generation + 1, 1),
        envelope: decrypted.envelope,
        sync: { linked: true, baseRevision: revision, dirty: false },
      };
      await this.store.write(replica);
      this.setSession(
        replica,
        decrypted.snapshot,
        decrypted.key,
        decrypted.material,
      );
      return cloneJson(replica);
    });
  }

  async resolveConflictWithRemote(
    envelopeValue: SerializedVaultEnvelope,
    revision: number,
    credential: VaultCredential | null = this.material,
  ): Promise<LocalReplica> {
    this.assertRemoteRevision(revision);
    if (!credential) throw new SecureVaultLockedError();
    return this.serialize(async () => {
      const current = await this.rawReplica();
      if (!current) {
        throw new SecureVaultConflictError(
          "Aucune copie locale ne peut recevoir la révision distante.",
        );
      }

      // Cette variante est réservée au choix explicite « utiliser le serveur »
      // présenté après un conflit. Le déchiffrement et la validation précèdent
      // toujours l'unique écriture qui remplace atomiquement la copie dirty.
      const decrypted = await this.decrypt(envelopeValue, credential);
      const replica: LocalReplica = {
        localVersion: SECURE_VAULT_LOCAL_VERSION,
        generation: Math.max(current.generation + 1, 1),
        envelope: decrypted.envelope,
        sync: { linked: true, baseRevision: revision, dirty: false },
      };
      await this.store.write(replica);
      this.setSession(
        replica,
        decrypted.snapshot,
        decrypted.key,
        decrypted.material,
      );
      return cloneJson(replica);
    });
  }

  async markRemoteConfirmed(
    revision: number,
    exactEnvelope?: SerializedVaultEnvelope,
  ): Promise<boolean> {
    this.assertRemoteRevision(revision);
    return this.serialize(async () => {
      const current = await this.rawReplica();
      if (!current) return false;
      if (current.sync.dirty && !exactEnvelope) return false;
      if (
        exactEnvelope &&
        JSON.stringify(validateEnvelope(exactEnvelope)) !==
          JSON.stringify(current.envelope)
      ) {
        return false;
      }
      const confirmed: LocalReplica = {
        ...current,
        sync: { linked: true, baseRevision: revision, dirty: false },
      };
      await this.store.write(confirmed);
      if (this.replica) this.replica = cloneJson(confirmed);
      return true;
    });
  }

  /**
   * Confirme exactement l’enveloppe observée et capture, dans la même section
   * sérialisée, les opérations qu’elle contient. `null` signifie que la copie
   * locale a changé : aucun appel D1 ne doit alors partir pour cette candidate.
   */
  async markRemoteConfirmedAndCaptureMutations(
    revision: number,
    exactEnvelope: SerializedVaultEnvelope,
  ): Promise<OfflineMovementOperation[] | null> {
    this.assertRemoteRevision(revision);
    const normalizedEnvelope = validateEnvelope(exactEnvelope);
    return this.serialize(async () => {
      const current = await this.rawReplica();
      if (
        !current ||
        JSON.stringify(normalizedEnvelope) !== JSON.stringify(current.envelope)
      ) {
        return null;
      }
      if (!this.plaintext || !this.replica || !this.key) {
        throw new SecureVaultLockedError();
      }
      const confirmed: LocalReplica = {
        ...current,
        sync: { linked: true, baseRevision: revision, dirty: false },
      };
      if (
        current.sync.linked !== confirmed.sync.linked ||
        current.sync.baseRevision !== confirmed.sync.baseRevision ||
        current.sync.dirty !== confirmed.sync.dirty
      ) {
        await this.store.write(confirmed);
      }
      this.replica = cloneJson(confirmed);
      return cloneJson(this.plaintext.mutations);
    });
  }

  private metadata(replica: LocalReplica): VaultSyncMetadata {
    return {
      linked: replica.sync.linked,
      generation: replica.generation,
      baseRevision: replica.sync.baseRevision,
      dirty: replica.sync.dirty,
    };
  }

  async getSyncMetadata(): Promise<VaultSyncMetadata> {
    await this.settleWrites();
    const replica = await this.rawReplica();
    if (!replica) {
      throw new SecureVaultUnavailableError(
        "Aucune copie locale chiffrée n’est disponible.",
      );
    }
    return this.metadata(replica);
  }

  private async readSnapshot<T>(reader: (snapshot: SecureVaultSnapshot) => T): Promise<T> {
    await this.settleWrites();
    await this.waitUntilUnlocked();
    if (!this.plaintext) throw new SecureVaultLockedError();
    return cloneJson(reader(this.plaintext));
  }

  private mutateSnapshot(
    mutation: (snapshot: SecureVaultSnapshot) => boolean | void,
  ): Promise<boolean> {
    return this.serialize(async () => {
      if (!this.plaintext || !this.replica || !this.key) {
        throw new SecureVaultLockedError();
      }
      if (this.replica.generation >= Number.MAX_SAFE_INTEGER) {
        throw new SecureVaultUnavailableError(
          "La génération du coffre local a atteint sa limite.",
        );
      }
      const nextSnapshot = cloneJson(this.plaintext);
      if (mutation(nextSnapshot) === false) return false;
      assertSnapshot(nextSnapshot);
      const salt = base64ToBytes(
        this.replica.envelope.salt,
        "Le sel PBKDF2",
      );
      const envelope = await this.encrypt(
        nextSnapshot,
        this.key,
        salt,
        this.replica.envelope.iterations,
        this.replica.envelope.iv,
      );
      const nextReplica: LocalReplica = {
        localVersion: SECURE_VAULT_LOCAL_VERSION,
        generation: this.replica.generation + 1,
        envelope,
        sync: { ...this.replica.sync, dirty: true },
      };
      await this.store.write(nextReplica);
      this.plaintext = nextSnapshot;
      this.replica = nextReplica;
      return true;
    });
  }

  async getCache(key: string): Promise<OfflineCacheEntry | null> {
    if (!boundedString(key, MAX_CACHE_KEY_LENGTH)) return null;
    return this.readSnapshot((snapshot) =>
      snapshot.cache.find((entry) => entry.key === key) ?? null,
    );
  }

  async listCache(): Promise<OfflineCacheEntry[]> {
    return this.readSnapshot((snapshot) => snapshot.cache);
  }

  async putCache(entry: OfflineCacheEntry): Promise<void> {
    const normalized = cloneJson(entry);
    assertCacheEntry(normalized);
    await this.mutateSnapshot((snapshot) => {
      const index = snapshot.cache.findIndex(
        (candidate) => candidate.key === normalized.key,
      );
      if (index === -1) snapshot.cache.push(normalized);
      else snapshot.cache[index] = normalized;
    });
  }

  async commitSnapshot(
    entries: OfflineCacheEntry[],
    mutations: OfflineMovementOperation[],
  ): Promise<boolean> {
    const normalized = cloneJson(entries);
    const normalizedMutations = cloneJson(mutations);
    const keys = new Set<string>();
    for (const entry of normalized) {
      assertCacheEntry(entry);
      if (keys.has(entry.key)) {
        throw new TypeError("Le remplacement du cache contient une clé dupliquée.");
      }
      keys.add(entry.key);
    }
    const operationIds = new Set<string>();
    for (const operation of normalizedMutations) {
      assertMovementOperation(operation);
      if (operationIds.has(operation.client_operation_id)) {
        throw new TypeError(
          "Le remplacement de projection contient une mutation dupliquée.",
        );
      }
      operationIds.add(operation.client_operation_id);
    }
    return this.mutateSnapshot((snapshot) => {
      const currentByKey = new Map(
        snapshot.cache.map((entry) => [entry.key, entry]),
      );
      const sameCache =
        snapshot.cache.length === normalized.length &&
        normalized.every((entry) => {
          const current = currentByKey.get(entry.key);
          return (
            current !== undefined &&
            JSON.stringify(current.value) === JSON.stringify(entry.value)
          );
        });
      const currentMutationsById = new Map(
        snapshot.mutations.map((operation) => [
          operation.client_operation_id,
          operation,
        ]),
      );
      const sameMutations =
        snapshot.mutations.length === normalizedMutations.length &&
        normalizedMutations.every((operation) => {
          const current = currentMutationsById.get(
            operation.client_operation_id,
          );
          return (
            current !== undefined &&
            JSON.stringify(current) === JSON.stringify(operation)
          );
        });
      if (sameCache && sameMutations) {
        return false;
      }
      snapshot.cache = normalized;
      snapshot.mutations = normalizedMutations;
      return true;
    });
  }

  async deleteCache(key: string): Promise<void> {
    await this.mutateSnapshot((snapshot) => {
      snapshot.cache = snapshot.cache.filter((entry) => entry.key !== key);
    });
  }

  async clearCache(): Promise<void> {
    await this.mutateSnapshot((snapshot) => {
      snapshot.cache = [];
    });
  }

  async listMutations(): Promise<OfflineMovementOperation[]> {
    return this.readSnapshot((snapshot) =>
      [...snapshot.mutations].sort(
        (left, right) => left.sequence - right.sequence,
      ),
    );
  }

  async putMutation(operation: OfflineMovementOperation): Promise<void> {
    const normalized = cloneJson(operation);
    assertMovementOperation(normalized);
    await this.mutateSnapshot((snapshot) => {
      const index = snapshot.mutations.findIndex(
        (candidate) =>
          candidate.client_operation_id === normalized.client_operation_id,
      );
      if (index === -1) snapshot.mutations.push(normalized);
      else snapshot.mutations[index] = normalized;
    });
  }

  async deleteMutation(clientOperationId: string): Promise<void> {
    await this.mutateSnapshot((snapshot) => {
      snapshot.mutations = snapshot.mutations.filter(
        (operation) =>
          operation.client_operation_id !== clientOperationId,
      );
    });
  }

  async getReceipt(
    clientOperationId: string,
  ): Promise<OfflineMovementOperation | null> {
    return this.readSnapshot((snapshot) =>
      snapshot.receipts.find(
        (operation) => operation.client_operation_id === clientOperationId,
      ) ?? null,
    );
  }

  async completeMutation(operation: OfflineMovementOperation): Promise<void> {
    const receipt = cloneJson({
      ...operation,
      status: "sync" as const,
      error: null,
    });
    assertMovementOperation(receipt);
    await this.mutateSnapshot((snapshot) => {
      snapshot.mutations = snapshot.mutations.filter(
        (candidate) =>
          candidate.client_operation_id !== receipt.client_operation_id,
      );
      const index = snapshot.receipts.findIndex(
        (candidate) =>
          candidate.client_operation_id === receipt.client_operation_id,
      );
      if (index === -1) snapshot.receipts.push(receipt);
      else snapshot.receipts[index] = receipt;
    });
  }

  async clearMutations(): Promise<void> {
    await this.mutateSnapshot((snapshot) => {
      snapshot.mutations = [];
      snapshot.receipts = [];
    });
  }

  async clearAll(): Promise<void> {
    await this.mutateSnapshot((snapshot) => {
      snapshot.cache = [];
      snapshot.mutations = [];
      snapshot.receipts = [];
    });
  }
}

let browserAdapter: SecureOfflineAdapter | null | undefined;

export function getSecureOfflineAdapter(): SecureOfflineAdapter | null {
  if (typeof globalThis.indexedDB === "undefined") return null;
  if (browserAdapter === undefined) {
    browserAdapter = new SecureOfflineAdapter(
      new IndexedDbSecureVaultStore(globalThis.indexedDB),
    );
  }
  return browserAdapter;
}
